import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { queryKeys } from "../lib/queryKeys";
import {
  complaintService,
  rmaService,
  issueTrackerService,
} from "../services/svc_qualityrecords";
import {
  ComplaintUpdate,
  IssueRecordCreate,
  IssueRecordUpdate,
} from "../types/qualityRecordTypes";
import { IdAsUUID } from "../types/common/commonTypes";

export const useTSQComplaint = (id: string | null, enabled: boolean) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.complaint.detail(id!),
    queryFn: () => complaintService.getById(id!),
    enabled: !!id && enabled,
  });

  const invalidate = () => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.complaint.detail(id!),
    });
  };

  return {
    ...query,
    invalidate,
  };
};

export const useTSMCreateComplaint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: complaintService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.complaint.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to create CF:", error);
    },
  });
};

export const useTSMUpdateComplaint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ComplaintUpdate }) =>
      complaintService.update(id, data),
    onSuccess: (data, variables) => {
      if (data.ok) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.complaint.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.complaint.detail(variables.id),
        });
      }
    },
    onError: (error) => {
      console.error("Failed to create CF:", error);
    },
  });
};

export const useTSMPostProcessComplaint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => complaintService.postProcess(id),
    onSuccess: (data, variables) => {
      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.complaint.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.complaint.detail(variables.id),
        });
      }
    },
    onError: (error) => {
      console.error("Failed to perform Complaint Post Processing:", error);
    },
  });
};

export const useTSMApproveComplaint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => complaintService.approve(id),
    onSuccess: () => {},
    onError: (error) => {
      console.error("Failed to approve Complaint:", error);
    },
  });
};

export const useTSMCreateIssueRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: issueTrackerService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.issuetracking.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to create Issue Record:", error);
    },
  });
};

export const useTSMUpdateIssueRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: IdAsUUID; data: IssueRecordUpdate }) =>
      issueTrackerService.update(data, id),
    onSuccess: (data, variables) => {
      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.issuetracking.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.issuetracking.detail(variables.id),
        });
      }
    },
    onError: (error) => {
      console.error("Failed to update Issue Record:", error);
    },
  });
};
